import { Alert, FieldContainer, Input, Modal } from '@odin-labs/components';
import {
  SiteDeviceRefusal,
  useAddSiteControllerMutation,
} from 'apollo/generated/client-operations';
import { AlertService } from 'components/alertService';
import { PlusIcon } from 'components/icons';
import React from 'react';
import { AuthContext } from 'auth';
import { SiteControllerPicker } from './SiteControllerPicker';
import {
  addOutcome,
  lostResponseOutcome,
  thrownOutcome,
} from './siteDeviceResults';
import {
  type ReloadSiteDevices,
  useSiteDeviceCommand,
} from './useSiteDeviceCommand';

type AddSiteControllerDrawerProps = {
  isOpen: boolean;
  siteId: string;
  onClose: () => void;
  /** The site's devices changed, or may have: read them again. */
  onChanged: ReloadSiteDevices;
  /** The serial numbers of the controllers on the site now, so a lost add is not mistaken for one already there. */
  knownSerialNumbers: readonly (string | null)[];
};

const sameSerial = (left: string | null | undefined, right: string): boolean =>
  (left ?? '').trim().toLowerCase() === right.trim().toLowerCase();

/**
 * Adds a controller that already exists in Avigilon by its serial number,
 * under the site's own Avigilon Access connector, which the API finds. A
 * refusal or a provider failure keeps the drawer and the serial number for
 * another try; an applied add closes it. A lost answer reloads the site,
 * and reports the add as done when the controller is there; a reload that
 * fails keeps submits off until the drawer is opened again. The drawer
 * stays open while a command runs.
 */
export function AddSiteControllerDrawer({
  isOpen,
  siteId,
  onClose,
  onChanged,
  knownSerialNumbers,
}: AddSiteControllerDrawerProps): React.ReactElement {
  const [serialNumber, setSerialNumber] = React.useState('');
  const auth = React.useContext(AuthContext);
  const canFetch = Boolean(auth?.currentUser?.isAdmin);
  React.useEffect(() => setSerialNumber(''), [siteId, isOpen]);
  const { outcome, setOutcome, busy, stale, run } = useSiteDeviceCommand({
    open: isOpen,
    onChanged,
  });
  const [add] = useAddSiteControllerMutation();

  const close = (): void => {
    if (busy) return;
    setSerialNumber('');
    onClose();
  };
  const closeWith = (title: string, message: string): void => {
    setSerialNumber('');
    onClose();
    AlertService.alert('success', title, message);
  };

  const submit = (): Promise<void> => {
    const serial = serialNumber.trim();
    const knownBefore = knownSerialNumbers.some((known) =>
      sameSerial(known, serial),
    );
    return run({
      command: async () => {
        try {
          const { data } = await add({
            variables: { input: { siteId, serialNumber: serial } },
          });
          return data
            ? addOutcome(data.addSiteController)
            : lostResponseOutcome('addition');
        } catch (error) {
          return thrownOutcome('added', 'addition', error);
        }
      },
      onApplied: (result) => closeWith(result.title, result.message),
      onReloaded: (result, controllers) => {
        if (
          result.refusal === SiteDeviceRefusal.AlreadyAssigned &&
          controllers.some((row) => sameSerial(row.serialNumber, serial))
        )
          setOutcome({
            ...result,
            message: 'This controller is already on this site.',
          });
      },
      onLost: (controllers) => {
        if (
          !knownBefore &&
          controllers.some((row) => sameSerial(row.serialNumber, serial))
        )
          closeWith(
            'Controller added',
            'Odin did not answer, but the controller is now on this site.',
          );
      },
    });
  };

  const canSubmit = serialNumber.trim().length > 0 && !busy && !stale;

  return (
    <Modal
      open={isOpen}
      setOpen={(open) => !open && close()}
      overlayCloseDisabled={busy}
      variant="drawer"
      size="sm"
      title="Add a controller"
      onAction={submit}
      actionText="Add controller"
      actionIcon={PlusIcon}
      actionButtonEnabled={canSubmit}
      actionButtonWithSpinner={busy}
      onCancel={close}
      cancelText="Cancel"
      cancelButtonEnabled={!busy}
    >
      <div className="odin-space-y-4 odin-text-sm odin-text-gray-600">
        <p>
          Enter the full serial number of a controller that already exists in
          Avigilon. Odin records it with its ports and readers, together or not
          at all, and writes nothing to Avigilon.
        </p>
        {isOpen && canFetch && (
          <SiteControllerPicker
            key={siteId}
            siteId={siteId}
            serialNumber={serialNumber}
            disabled={busy}
            onChoose={setSerialNumber}
          />
        )}
        <FieldContainer name="serialNumber" label="Serial number">
          <Input
            name="serialNumber"
            type="text"
            value={serialNumber}
            onChange={setSerialNumber}
            placeholder="For example 4K20D1F7"
            disabled={busy}
          />
        </FieldContainer>
        {outcome && (
          <Alert
            type={outcome.type}
            text={`${outcome.title}. ${outcome.message}`}
          />
        )}
      </div>
    </Modal>
  );
}
